import React, { useEffect, useRef } from "react";

interface ReCaptchaProps {
  siteKey: string;
  action: string;
  name?: string;
  onTokenRefreshed: (token: string) => void;
  /**
   * Increment this counter to immediately fetch a fresh token.
   * Useful after a failed form submission where the previous token was consumed.
   * The initial value of 0 is ignored — only increments trigger a refresh.
   */
  refreshTrigger?: number;
}

/**
 * ReCaptcha v3 component that generates and auto-refreshes tokens.
 *
 * This component renders a hidden input field containing the ReCaptcha token.
 * The token is automatically refreshed every 2 minutes (before the 2-minute timeout).
 *
 * @param siteKey - The ReCaptcha v3 site key
 * @param action - The action name for this ReCaptcha (e.g., "provet_booking")
 * @param name - The name attribute for the hidden input (default: "ReCaptchaResponse")
 *
 * @example
 * ```tsx
 * <ReCaptcha siteKey="your-site-key" action="booking" />
 * ```
 */
export const ReCaptcha = ({
  siteKey,
  action,
  name = "ReCaptchaResponse",
  onTokenRefreshed,
  refreshTrigger
}: ReCaptchaProps) => {
  const inputRef = useRef<HTMLInputElement>(null);

  // Always-current ref so the trigger effect below never captures a stale callback
  const onTokenRefreshedRef = useRef(onTokenRefreshed);
  useEffect(() => {
    onTokenRefreshedRef.current = onTokenRefreshed;
  });

  useEffect(() => {
    if (!siteKey) {
      return;
    }

    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let isMounted = true;

    const refreshReCaptcha = async () => {
      try {
        const token = await grecaptcha.execute(siteKey, { action });

        if (isMounted && inputRef.current) {
          inputRef.current.value = token;

          onTokenRefreshed(token);
        }

        // ReCaptcha tokens have a 2 minute timeout, refresh before expiry
        if (isMounted) {
          timeoutId = setTimeout(refreshReCaptcha, 1000 * 60 * 2 - 5000);
        }
      } catch (error) {
        console.error("ReCaptcha token refresh failed:", error);
        // Retry after 5 seconds on failure
        if (isMounted) {
          timeoutId = setTimeout(refreshReCaptcha, 5000);
        }
      }
    };

    // Wait for grecaptcha to be available (handles async script loading)
    const waitForGrecaptcha = () => {
      if (typeof grecaptcha !== "undefined") {
        grecaptcha.ready(async () => {
          if (isMounted) {
            await refreshReCaptcha();
          }
        });
      } else if (isMounted) {
        // Script not loaded yet, check again in 100ms
        timeoutId = setTimeout(waitForGrecaptcha, 100);
      }
    };

    waitForGrecaptcha();

    return () => {
      isMounted = false;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [siteKey, action]);

  // When a parent requests an immediate refresh (e.g. after a failed submission),
  // fetch a fresh token without touching the periodic-refresh schedule above.
  // refreshTrigger === 0 is the initial/unset state and is intentionally skipped.
  useEffect(() => {
    if (!refreshTrigger || !siteKey) {
      return;
    }

    if (typeof grecaptcha === "undefined") {
      return;
    }

    let cancelled = false;

    grecaptcha.ready(async () => {
      if (cancelled) {
        return;
      }
      try {
        const token = await grecaptcha.execute(siteKey, { action });
        if (!cancelled && inputRef.current) {
          inputRef.current.value = token;
          onTokenRefreshedRef.current(token);
        }
      } catch (error) {
        console.error("ReCaptcha manual refresh failed:", error);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [refreshTrigger, siteKey, action]);

  return <input ref={inputRef} type="hidden" name={name} />;
};
